import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { AlertCircle, Eye, EyeOff, LoaderCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useSignIn } from '../hooks/useSignIn'
import { authFeatureService } from '../services/auth.feature.service'
import { authErrorMessage } from '../lib/authErrorMessage'
import type { SignInFormValues } from '../types'
import { PasswordResetDialog } from './PasswordResetDialog'

export function SignInForm({ returnTo = '/dashboard' }: { returnTo?: string }) {
  const { signIn, error, loading, clearError } = useSignIn(returnTo)
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignInFormValues>({ mode: 'onBlur' })
  const navigate = useNavigate()
  const [googleLoading, setGoogleLoading] = useState(false)
  const [googleError, setGoogleError] = useState<string | null>(null)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const email = watch('email', '')
  const busy = googleLoading || loading

  async function handleGoogleSignIn() {
    clearError()
    setGoogleError(null)
    setGoogleLoading(true)
    try {
      await authFeatureService.signInWithGoogle()
      await navigate(returnTo)
    } catch (err) {
      setGoogleError(authErrorMessage(err, 'google-sign-in'))
    } finally {
      setGoogleLoading(false)
    }
  }

  async function handleEmailSignIn(values: SignInFormValues) {
    setGoogleError(null)
    await signIn({ ...values, email: values.email.trim() })
  }

  return (
    <div className="space-y-6">
      <Button
        type="button"
        variant="outline"
        className="google-sign-in-button h-11 w-full rounded-full shadow-xs"
        disabled={busy}
        aria-busy={googleLoading}
        onClick={() => void handleGoogleSignIn()}
      >
        {googleLoading ? (
          <LoaderCircle
            className="size-5 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : (
          <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        )}
        {googleLoading ? 'Opening Google…' : 'Sign in with Google'}
      </Button>

      {googleError && (
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertDescription>{googleError}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-3" aria-hidden="true">
        <Separator className="flex-1" />
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          or use email
        </span>
        <Separator className="flex-1" />
      </div>

      <form
        onSubmit={(event) => void handleSubmit(handleEmailSignIn)(event)}
        className="space-y-5"
        noValidate
        aria-busy={loading}
      >
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              className={cn(
                'h-11 rounded-lg bg-background/70 transition-[height,padding]',
                errors.email && 'h-16 pt-1 pb-6'
              )}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'email-error' : undefined}
              {...register('email', {
                required: 'Enter your email address.',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address.',
                },
                onChange: clearError,
              })}
            />
            {errors.email && (
              <p
                id="email-error"
                className="pointer-events-none absolute right-3 bottom-1.5 left-3 text-xs leading-4 text-destructive"
              >
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="password">Password</Label>
            <button
              type="button"
              className="rounded-sm text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setResetOpen(true)}
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={passwordVisible ? 'text' : 'password'}
              autoComplete="current-password"
              className={cn(
                'h-11 rounded-lg bg-background/70 pr-11 transition-[height,padding]',
                errors.password && 'h-16 pt-1 pr-11 pb-6'
              )}
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? 'password-error' : undefined}
              {...register('password', {
                required: 'Enter your password.',
                onChange: clearError,
              })}
            />
            <button
              type="button"
              className="absolute top-0 right-0 flex h-11 w-11 items-center justify-center rounded-r-lg text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
              aria-pressed={passwordVisible}
              onClick={() => setPasswordVisible((visible) => !visible)}
            >
              {passwordVisible ? (
                <EyeOff className="size-4" aria-hidden="true" />
              ) : (
                <Eye className="size-4" aria-hidden="true" />
              )}
            </button>
            {errors.password && (
              <p
                id="password-error"
                className="pointer-events-none absolute right-3 bottom-1.5 left-3 text-xs leading-4 text-destructive"
              >
                {errors.password.message}
              </p>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" size="lg" className="h-11 w-full rounded-full" disabled={busy}>
          {loading && (
            <LoaderCircle
              className="size-5 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
          {loading ? 'Signing you in…' : 'Sign in'}
        </Button>
      </form>

      <div className="space-y-3 border-t border-border/70 pt-5 text-center">
        <p className="text-sm text-muted-foreground">New to One Day Dance?</p>
        <Button asChild variant="outline" className="h-11 w-full rounded-full">
          <Link to="/register" state={{ returnTo }}>
            Create account
          </Link>
        </Button>
      </div>

      <PasswordResetDialog email={email} open={resetOpen} onOpenChange={setResetOpen} />
    </div>
  )
}
